import React, { createContext, useState, useEffect, useCallback, useRef, useContext } from 'react';
import subscriptionService from '../services/subscriptionService';
import { AuthContext } from './AuthContext';

export const SubscriptionContext = createContext(null);

// Polling interval in milliseconds (5 minutes)
const POLLING_INTERVAL = 5 * 60 * 1000;

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollingIntervalRef = useRef(null);
  const { user, isAuthenticated } = useContext(AuthContext);

  /**
   * Fetch subscription status from API
   * @param {boolean} showLoading - Whether to show loading state
   */
  const fetchSubscriptionStatus = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const data = await subscriptionService.getStatus();
      setSubscription(data);
      
      return data;
    } catch (err) {
      // If user is not authenticated or has no subscription, don't treat as error
      if (err.response?.status === 401 || err.response?.status === 404) {
        setSubscription(null);
      } else {
        const errorMessage = err.response?.data?.error?.message || 'Failed to fetch subscription status';
        setError(errorMessage);
        console.error('Failed to fetch subscription status:', err);
        console.error('Error details:', err.response?.data);
      }
      return null;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Start polling for subscription status updates
   */
  const startPolling = useCallback(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Set up new polling interval
    pollingIntervalRef.current = setInterval(() => {
      fetchSubscriptionStatus(false); // Don't show loading on background polls
    }, POLLING_INTERVAL);
  }, [fetchSubscriptionStatus]);

  /**
   * Stop polling for subscription status updates
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Initialize subscription state and start polling
   * Re-fetch when user authentication state changes
   */
  useEffect(() => {
    if (isAuthenticated && user) {
      // User is logged in, fetch subscription status
      fetchSubscriptionStatus();
      startPolling();
    } else {
      // User is not logged in, clear subscription and stop polling
      setLoading(false);
      setSubscription(null);
      stopPolling();
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [isAuthenticated, user, fetchSubscriptionStatus, startPolling, stopPolling]);

  /**
   * Activate demo subscription
   * @returns {Promise<Object>} Subscription data
   */
  const activateDemo = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const data = await subscriptionService.activateDemo();
      setSubscription(data);
      
      // Restart polling after activation
      startPolling();
      
      return data;
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to activate demo';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [startPolling]);

  /**
   * Refresh subscription status
   * @returns {Promise<Object>} Updated subscription data
   */
  const refreshStatus = useCallback(async () => {
    return await fetchSubscriptionStatus(true);
  }, [fetchSubscriptionStatus]);

  /**
   * Cancel subscription
   * @returns {Promise<Object>} Cancellation confirmation
   */
  const cancelSubscription = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const data = await subscriptionService.cancel();
      
      // Refresh subscription status after cancellation
      await fetchSubscriptionStatus(false);
      
      return data;
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to cancel subscription';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSubscriptionStatus]);

  /**
   * Get subscription history
   * @returns {Promise<Array>} Array of subscription records
   */
  const getHistory = useCallback(async () => {
    try {
      setError(null);
      const data = await subscriptionService.getHistory();
      return data;
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to fetch subscription history';
      setError(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check if user has active subscription
   * @returns {boolean}
   */
  const hasActiveSubscription = subscription?.status === 'active';

  /**
   * Check if subscription is demo type
   * @returns {boolean}
   */
  const isDemoSubscription = subscription?.type === 'demo';

  /**
   * Check if subscription is premium/paid type
   * @returns {boolean}
   */
  const isPaidSubscription = subscription?.type === 'premium' || subscription?.type === 'paid';

  /**
   * Check if subscription is expired
   * @returns {boolean}
   */
  const isExpired = subscription?.status === 'expired' || subscription?.is_expired === true;

  /**
   * Get days remaining in subscription
   * @returns {number|null}
   */
  const daysRemaining = subscription?.days_remaining ?? null;

  const value = {
    subscription,
    loading,
    error,
    hasActiveSubscription,
    isDemoSubscription,
    isPaidSubscription,
    isExpired,
    daysRemaining,
    activateDemo,
    refreshStatus,
    cancelSubscription,
    getHistory,
    clearError,
    startPolling,
    stopPolling,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
